import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactPlayer from 'react-player';
import moment from 'moment';
import useImages from 'hooks/useGallery';
import { ui } from 'util/ui';
import ArchyModal from 'components/shell/ArchyModal';
import { PlayIcon, PauseIcon, DownloadIcon, CameraIcon, FrameStepIcon } from 'components/shell/Icons';
import { readMediaInfo, httpRange } from 'util/mediaInfo';
import useLabelNames from 'hooks/useLabelNames';

/**
 * Preview player, UI refresh styling (Aaron, 5 Nov 2026): the shared pop-up frame, a black video
 * area, then a light control bar (play/pause, a slim progress bar, time, Download, Screenshot).
 * Screenshots download and are saved to the project's gallery (task #15).
 */

type Props = {
  video: IVideo;
  exit(): void;
  downloadVideo(): void;
  url: string;
  /** The project the video belongs to: screenshots are saved to its gallery (task #15). */
  projectID?: string;
};

type LoadType = {
  played: number;
  playedSeconds: number;
  loaded: number;
  loadedSeconds: number;
};

const toolBtn: React.CSSProperties = {
  height: 36,
  padding: '0 12px',
  border: `1px solid ${ui.control}`,
  borderRadius: 10,
  background: ui.surface,
  color: ui.text2,
  font: 'inherit',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  whiteSpace: 'nowrap',
  flex: 'none',
};

/** A usable length in seconds, or 0 while it isn't known (browsers report NaN or Infinity until then). */
const knownLength = (d: number | undefined | null) => (typeof d === 'number' && isFinite(d) && d > 0 ? d : 0);

const two = (n: number) => (n < 10 ? `0${n}` : String(n));

/** Timecode with frames, HH:MM:SS:FF (Aaron, 5 Nov 2026). Frames count within each second of real time. */
const timecode = (seconds: number, fps: number): string => {
  const t = Math.max(0, knownLength(seconds) || 0);
  const whole = Math.floor(t + 1e-6);
  const perSecond = Math.max(1, Math.round(fps));
  const frame = Math.min(perSecond - 1, Math.floor((t - whole) * fps + 1e-6));
  return `${two(Math.floor(whole / 3600))}:${two(Math.floor((whole % 3600) / 60))}:${two(whole % 60)}:${two(frame)}`;
};

/** Frame rate assumed until the file's own is known: Australian (PAL) video. */
const DEFAULT_FPS = 25;

export const VideoPlayer: React.FC<Props> = ({ video, exit, downloadVideo, url, projectID }) => {
  const player = useRef<ReactPlayer>(null);
  const { uploadImages } = useImages();
  const labelNames = useLabelNames();
  const [shotNote, setShotNote] = useState('');
  const [playing, setPlaying] = useState(true);
  const [played, setPlayed] = useState(0);
  const [loaded, setLoaded] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [duration, setDuration] = useState(0);
  const [fps, setFps] = useState(DEFAULT_FPS);
  const [fpsKnown, setFpsKnown] = useState(false);

  // The exact frame rate from the file's header (MP4 / MOV), read with a few small range requests.
  useEffect(() => {
    if (!url) return;
    let live = true;
    readMediaInfo(httpRange(url), Number(video.size) || 0, video.id)
      .then((info) => {
        const v = info && info.tracks.find((t) => t.kind === 'video');
        if (live && v && v.frameRate && isFinite(v.frameRate)) {
          setFps(v.frameRate);
          setFpsKnown(true);
        }
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [url, video.id, video.size]);

  /** Move by whole frames (or seconds). Pauses first, so the frame stays on screen. */
  const step = useCallback(
    (frames: number) => {
      const el = player.current ? (player.current.getInternalPlayer() as HTMLVideoElement | null) : null;
      if (!el || typeof el.currentTime !== 'number') return;
      setPlaying(false);
      el.pause();
      const length = knownLength(el.duration) || duration;
      const current = Math.floor(el.currentTime * fps + 1e-6);
      const lastFrame = length ? Math.max(0, Math.floor(length * fps) - 1) : Infinity;
      const target = Math.max(0, Math.min(lastFrame, current + frames));
      // The middle of the frame, so the browser shows exactly that frame.
      el.currentTime = (target + 0.5) / fps;
    },
    [fps, duration],
  );

  // Keys: ← → one frame, Shift+← → one second, Space play/pause. Not while typing in a field.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t && (t.tagName === 'TEXTAREA' || (t.tagName === 'INPUT' && (t as HTMLInputElement).type !== 'range'));
      if (typing) return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        e.preventDefault();
        const dir = e.key === 'ArrowRight' ? 1 : -1;
        step(dir * (e.shiftKey ? Math.max(1, Math.round(fps)) : 1));
      } else if (e.key === ' ' && t.tagName !== 'BUTTON') {
        e.preventDefault();
        setPlaying((p) => !p);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [step, fps]);

  const screenShot = async () => {
    const videoPlayer = player.current ? (player.current.getInternalPlayer() as HTMLVideoElement) : null;
    if (!videoPlayer) return;
    const canvas = document.createElement('canvas');
    canvas.width = videoPlayer.videoWidth;
    canvas.height = videoPlayer.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx!.drawImage(videoPlayer, 0, 0, canvas.width, canvas.height);
    const dataURI = canvas.toDataURL('image/jpeg');

    const byteString = atob(dataURI.split(',')[1]);
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    const bytes = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
    const blob = new Blob([bytes], { type: mimeString });
    const fileName = `${video.id} - screenshot - ${moment().format('YYYYMMDDHHmmss')}.jpg`;

    // Task #15: save the frame to the project's gallery as well as downloading it.
    if (projectID) {
      setShotNote('Saving to gallery…');
      const shot = new File([blob], fileName, { type: mimeString });
      uploadImages(shot, projectID, () => undefined)
        .then((id) => setShotNote(id ? 'Saved to gallery' : 'Couldn’t save to gallery (downloaded only)'))
        .catch(() => setShotNote('Couldn’t save to gallery (downloaded only)'));
    }

    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const onProgress = (state: LoadType) => {
    if (isFinite(state.played)) setPlayed(state.played * 100);
    if (isFinite(state.loaded)) setLoaded(state.loaded * 100);
    setSeconds(state.playedSeconds);
    const d = knownLength(player.current ? player.current.getDuration() : 0);
    if (d) setDuration(d);
  };

  /**
   * 2024 brief #17, "the player sometimes has no end time". The length used to be read only while
   * the video played, and some files (fragmented MP4s, recorded WebM) don't state their length up
   * front, so the browser reports Infinity. Now: take the length as soon as the browser knows it,
   * follow any change, and for a file with no stated length jump far ahead once so the browser
   * works it out, then return to the start.
   */
  const probed = useRef(false);
  const onReady = () => {
    const el = player.current ? (player.current.getInternalPlayer() as HTMLVideoElement | null) : null;
    if (!el || typeof el.addEventListener !== 'function') return;
    const update = () => {
      const d = knownLength(el.duration);
      if (d) setDuration(d);
    };
    if (probed.current) return update();
    probed.current = true;
    el.addEventListener('durationchange', update);
    // After a frame step or a seek while paused, show the exact time (progress events stop when paused).
    el.addEventListener('seeked', () => {
      setSeconds(el.currentTime);
      const d = knownLength(el.duration);
      if (d) setPlayed((el.currentTime / d) * 100);
    });
    update();
    if (el.duration === Infinity) {
      const back = () => {
        el.removeEventListener('timeupdate', back);
        update();
        el.currentTime = 0;
      };
      el.addEventListener('timeupdate', back);
      el.currentTime = 1e101;
    }
  };

  const seek = (value: number) => {
    setPlayed(value);
    if (player.current) player.current.seekTo(value / 100);
  };

  return (
    <ArchyModal title={video.id} subtitle={labelNames.preview} onClose={exit} width={1000} media labelledBy="player-title">
      {/* Full width of the pop-up, 16:9, but never taller than the window allows; the video letterboxes inside. */}
      <div
        style={{
          background: '#000000',
          width: '100%',
          height: 'min(562px, calc((100vw - 32px) * 0.5625), calc(100vh - 220px))',
          display: 'flex',
        }}
      >
        <ReactPlayer
          url={url}
          playing={playing}
          width="100%"
          height="100%"
          onProgress={onProgress}
          onReady={onReady}
          onDuration={(d: number) => knownLength(d) && setDuration(d)}
          onEnded={() => setPlaying(false)}
          ref={player}
          progressInterval={100}
          config={{ file: { attributes: { crossOrigin: 'anonymous' } } }}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="archy-btn"
          aria-label={playing ? 'Pause' : 'Play'}
          title={playing ? 'Pause' : 'Play'}
          onClick={() => setPlaying(!playing)}
          style={{ width: 40, height: 40, flex: 'none', borderRadius: '50%', border: 'none', background: ui.accent, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
        >
          {playing ? <PauseIcon size={16} color="#FFFFFF" /> : <PlayIcon size={18} color="#FFFFFF" />}
        </button>
        <div style={{ flex: '1 1 240px', position: 'relative', height: 20, display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'absolute', left: 0, right: 0, height: 6, borderRadius: 999, background: ui.line, overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${loaded}%`, background: ui.control }} />
            <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${played}%`, background: ui.accent }} />
          </div>
          <input
            type="range"
            aria-label="Position"
            min={0}
            max={100}
            step={0.1}
            value={played}
            onChange={(e) => seek(parseFloat(e.target.value))}
            className="archy-scrub"
            style={{ position: 'relative', width: '100%', margin: 0, padding: 0, border: 'none', outline: 'none', boxShadow: 'none', background: 'transparent' }}
          />
        </div>
        <button
          type="button"
          className="archy-btn"
          aria-label="Back one frame"
          title="Back one frame (←). Shift+← goes back one second."
          onClick={() => step(-1)}
          style={{ ...toolBtn, width: 36, padding: 0, justifyContent: 'center' }}
        >
          <FrameStepIcon dir="back" size={14} />
        </button>
        <button
          type="button"
          className="archy-btn"
          aria-label="Forward one frame"
          title="Forward one frame (→). Shift+→ goes forward one second."
          onClick={() => step(1)}
          style={{ ...toolBtn, width: 36, padding: 0, justifyContent: 'center' }}
        >
          <FrameStepIcon dir="forward" size={14} />
        </button>
        <span
          title={`Hours:minutes:seconds:frames at ${Math.round(fps * 1000) / 1000} fps${fpsKnown ? '' : ' (assumed; the file did not say)'}`}
          style={{ fontSize: 13, color: ui.muted, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', flex: 'none' }}
        >
          <span style={{ color: ui.ink, fontWeight: 600 }}>{timecode(seconds, fps)}</span> / {duration ? timecode(duration, fps) : '––:––:––:––'}
        </span>
        <button type="button" className="archy-btn" style={toolBtn} onClick={() => downloadVideo()}>
          <DownloadIcon size={15} />
          Download
        </button>
        <button
          type="button"
          className="archy-btn"
          style={toolBtn}
          onClick={screenShot}
          title={projectID ? 'Downloads the frame and saves it to this project’s gallery' : 'Downloads the frame'}
        >
          <CameraIcon size={15} />
          Screenshot
        </button>
        {shotNote && (
          <span role="status" style={{ fontSize: 13, color: ui.muted, whiteSpace: 'nowrap' }}>
            {shotNote}
          </span>
        )}
      </div>
    </ArchyModal>
  );
};
